/* eslint-disable import/no-unused-modules */
/* eslint-disable no-unused-vars */
import * as Yup from 'yup';
import { VALIDATION_CONSTANTS } from '../constants';

/**
 * Validation schema names constants
 */
export const VALIDATION_SCHEMA_NAMES = {
    BASIC_INFO: 'basicInfoValidation',
    ASSET_SELECTION: 'assetSelectionValidation',
    PHYSICAL_CONNECTION: 'physicalConnectionValidation',
    REVIEW: 'reviewValidation'
};

// Basic Info Step Validation (Step 1)
const basicInfoValidation = Yup.object({
    powertrainName: Yup.string()
        .min(3, 'Powertrain name must be at least 3 characters')
        .max(VALIDATION_CONSTANTS.MAX_POWERTRAIN_NAME_LENGTH, 'Powertrain name must not exceed 100 characters')
        .required('Powertrain name is required')
        .matches(/^[a-zA-Z0-9\s\-_]+$/, 'Powertrain name can only contain letters, numbers, spaces, hyphens, and underscores'),

    selectedSite: Yup.object({
        siteId: Yup.number().required(),
        siteName: Yup.string().required(),
        organizationId: Yup.number().required()
    })
        .nullable()
        .required('Site selection is required'),

    selectedOrganization: Yup.object({
        organizationID: Yup.number().required(),
        name: Yup.string().required()
    })
        .nullable()
        .required('Organization selection is required'),

    description: Yup.string()
        .max(VALIDATION_CONSTANTS.MAX_DESCRIPTION_LENGTH, 'Description must not exceed 500 characters')
        .optional()
});

// Asset Selection Step Validation (Step 2)
const assetSelectionValidation = Yup.object({
    addedAssets: Yup.array()
        .of(
            Yup.object({
                assetId: Yup.number().required(),
                assetName: Yup.string().required(),
                assetType: Yup.string().required(),
                subType: Yup.string().optional(),
                isSelected: Yup.boolean().default(true)
            })
        )
        .min(1, 'At least one asset must be selected')
        .max(VALIDATION_CONSTANTS.MAX_SELECTED_ASSETS, 'Cannot select more than 50 assets')
        .required('Asset selection is required'),

    availableAssets: Yup.array()
        .of(
            Yup.object({
                assetId: Yup.number().required(),
                assetName: Yup.string().required(),
                assetType: Yup.string().required()
            })
        )
        .optional(),

    selectedAssetTypeAvailableSection: Yup.object({
        id: Yup.number().optional(),
        assetType: Yup.string().optional(),
        typeID: Yup.number().optional(),
        typeName: Yup.string().optional()
    })
        .nullable()
        .optional(),

    assetFilterAvailableSection: Yup.string()
        .max(100, 'Filter text too long')
        .optional()
});

// Physical Connection Step Validation (Step 3)
const physicalConnectionValidation = Yup.object({
    availableAssets: Yup.array()
        .of(
            Yup.object({
                id: Yup.string().required(),
                name: Yup.string().required(),
                type: Yup.string().required(),
                subType: Yup.string().optional(),
                connected: Yup.boolean().default(false)
            })
        )
        .optional(),

    configuredConnections: Yup.array()
        .of(
            Yup.object({
                id: Yup.string().required(),
                sourceAssetId: Yup.string().required(),
                targetAssetId: Yup.string().required(),
                connectionType: Yup.string()
                    .oneOf(['mechanical', 'electrical', 'data'], 'Invalid connection type')
                    .required('Connection type is required'),
                position: Yup.number()
                    .min(0, 'Position must be non-negative')
                    .optional()
            })
        )
        .optional(),

    connectionSequence: Yup.array()
        .of(Yup.string())
        .optional(),

    connectionSettings: Yup.object({
        allowParallelConnections: Yup.boolean().default(false),
        validateConnections: Yup.boolean().default(true),
        enforceSequence: Yup.boolean().default(true)
    })
        .optional()
});

/**
 * Review Step Validation (Step 3) - Final validation
 */
const reviewValidation = Yup.object({
    powertrainName: Yup.string()
        .required('Powertrain name is required'),

    selectedSite: Yup.object()
        .nullable()
        .required('Site selection is required'),

    selectedOrganization: Yup.object()
        .nullable()
        .required('Organization selection is required'),

    addedAssets: Yup.array()
        .min(1, 'At least one asset must be selected')
        .required('Asset selection is required'),

    agreesToTerms: Yup.boolean()
        .oneOf([true], 'You must agree to the terms and conditions')
        .optional(),

    finalReviewCompleted: Yup.boolean()
        .oneOf([true], 'Please complete the final review')
        .optional()
});

/**
 * Dynamic validation schema factory
 */
export const getValidationSchema = (schemaName, context = {}) => {
    const { isEditMode = false } = context;

    const schemas = {
        [VALIDATION_SCHEMA_NAMES.BASIC_INFO]: basicInfoValidation,
        [VALIDATION_SCHEMA_NAMES.ASSET_SELECTION]: assetSelectionValidation,
        [VALIDATION_SCHEMA_NAMES.PHYSICAL_CONNECTION]: physicalConnectionValidation,
        [VALIDATION_SCHEMA_NAMES.REVIEW]: reviewValidation
    };

    let baseSchema = schemas[schemaName];

    if (!baseSchema) {
        return Yup.object();
    }

    // Add edit mode specific validations if needed
    if (isEditMode && schemaName === VALIDATION_SCHEMA_NAMES.BASIC_INFO) {
        const editValidations = {
            powertrainId: Yup.number()
                .positive('Invalid powertrain ID')
                .required('Powertrain ID is required for editing')
        };
        baseSchema = baseSchema.concat(Yup.object(editValidations));
    }

    return baseSchema;
};

/**
 * Validation helper functions
 */
export const validateStepData = async (schemaName, data, context = {}) => {
    try {
        const schema = getValidationSchema(schemaName, context);
        await schema.validate(data, { abortEarly: false });

        return {
            isValid: true,
            errors: []
        };
    } catch (error) {
        const errors = error.inner?.map(err => ({
            field: err.path,
            message: err.message,
            value: err.value
        })) || [{ field: 'general', message: error.message }];

        return {
            isValid: false,
            errors
        };
    }
};

export const validateAllSteps = async (allStepData, context = {}) => {
    const stepSchemas = [
        { name: VALIDATION_SCHEMA_NAMES.BASIC_INFO, data: allStepData.basicInfo },
        { name: VALIDATION_SCHEMA_NAMES.ASSET_SELECTION, data: allStepData.assetSelection },
        { name: VALIDATION_SCHEMA_NAMES.PHYSICAL_CONNECTION, data: allStepData.physicalConnection },
        { name: VALIDATION_SCHEMA_NAMES.REVIEW, data: allStepData.review }
    ];

    const validationResults = {};
    let hasErrors = false;

    for (const { name, data } of stepSchemas) {
        if (data) {
            const result = await validateStepData(name, data, context);
            validationResults[name] = result;
            if (!result.isValid) {
                hasErrors = true;
            }
        }
    }

    return {
        isValid: !hasErrors,
        results: validationResults
    };
};

/**
 * Get field-specific error message
 */
export const getFieldError = (validationErrors, fieldPath) => {
    if (!validationErrors || !validationErrors.length) { return null; }

    const error = validationErrors.find(err => err.field === fieldPath);
    return error?.message || null;
};

/**
 * Check if specific field has errors
 */
export const hasFieldError = (validationErrors, fieldPath) => {
    return !!getFieldError(validationErrors, fieldPath);
};

/**
 * Get all error messages as an array
 */
export const getAllErrorMessages = (validationErrors) => {
    if (!validationErrors || !validationErrors.length) { return []; }

    return validationErrors.map(error => error.message);
};

/**
 * Export all validation schemas for direct use if needed
 */
export const validationSchemas = {
    basicInfoValidation,
    assetSelectionValidation,
    physicalConnectionValidation,
    reviewValidation
};

export default {
    VALIDATION_SCHEMA_NAMES,
    getValidationSchema,
    validateStepData,
    validateAllSteps,
    getFieldError,
    hasFieldError,
    getAllErrorMessages,
    validationSchemas
};
